import ky from 'ky';

import {
  ExtensionComponents,
  ExtensionFeatureSet,
  ServiceLoggerProperties,
} from './constants';

export type NamespacedAction =
  | {
      namespace: 'UncaughtError';
      action: 'ReactRender';
    }
  | {
      namespace: 'ContextObserver';
      action: 'observe';
    }
  | {
      namespace: 'Component';
      action: 'Render' | 'Inject' | 'Error';
    }
  | {
      namespace: 'Login';
      action: 'success' | 'failure' | 'click-reconnect' | 'reauth';
    }
  | {
      namespace: 'GEAuth';
      action:
        | 'refreshToken'
        | 'tokensExpired'
        | 'processOAuthResponse'
        | 'signinOpened'
        | 'signinStatusOpened';
    }
  | {
      namespace: 'Email';
      action:
        | 'OnMessageSend'
        | 'SendWithGroove'
        | 'OpenTemplateDialogCommand'
        | 'Tracking'
        | 'Add Specific Times Success'
        | 'Add Specific Times Error'
        | 'Add Specific Times Retry'
        | 'Copy Specific Times Success'
        | 'Copy Specific Times Error'
        | 'Insert Template'
        | 'Apply Blocklist';
    }
  | {
      namespace: 'Analytics';
      action: 'query-error';
    }
  | {
      namespace: 'OutlookEntrypoint';
      action:
        | 'userEmailAddressFail'
        | 'itemIdFail'
        | 'subjectFail'
        | 'refetchDataFail'
        | 'initializeSuggestedRecordsFail'
        | 'initializeRecipientsFail'
        | 'toRecipientsFail'
        | 'ccRecipientsFail'
        | 'bccRecipientsFail'
        | 'fromAddressFail'
        | 'addPaddingForEdgeFail'
        | 'getSubjectFromOutlookFail'
        | 'rootSagaFail';
    }
  | {
      namespace: 'Calendar';
      action:
        | 'Log'
        | 'Delete'
        | 'log-to-salesforce-button-clicked'
        | 'log-checkbox-click';
    }
  | {
      namespace: 'Flows';
      action: 'Add';
    }
  | {
      namespace: 'Credentials';
      action: 'encryption' | 'retrieve' | 'store' | 'clear';
    }
  | {
      namespace: 'Notification';
      action:
        | 'service-worker-launch'
        | 'FCM-token-registration'
        | 'FCM-payload-received';
    };

export type ConsoleMethodName = 'log' | 'info' | 'warn' | 'error';

type SplunkEventBase = {
  level: ConsoleMethodName;
};

type SplunkEventConsole = {
  message: unknown[];
} & SplunkEventBase;

type InternalEventDataReservedKeys = 'featureSet' | 'component';

type InternalEventDataGeneric = {
  [key in string]: unknown;
} & {
  [key in InternalEventDataReservedKeys]?: never;
};

type InternalEventDataComponent = {
  [key: string]: unknown;
  featureSet: ExtensionFeatureSet;
  component: ExtensionComponents;
};

export type InternalEventData =
  | InternalEventDataGeneric
  | InternalEventDataComponent;

type SplunkEventInternalObserve = {
  data: InternalEventData;
} & NamespacedAction &
  SplunkEventBase;

type SplunkEventInternalError = {
  data: InternalEventData;
  error: {
    name: string;
    message: string;
    stack: string;
  };
} & NamespacedAction &
  SplunkEventBase;

type SplunkEvent =
  | {
      tag: 'console';
      eventPayload: SplunkEventConsole;
    }
  | {
      tag: 'internalObserve';
      eventPayload: SplunkEventInternalObserve;
    }
  | {
      tag: 'internalError';
      eventPayload: SplunkEventInternalError;
    };

type SplunkEventTimestamped = SplunkEvent & { clientTimestamp: number };

type SplunkLoggerProperties = {
  [key: string]: unknown;
  service: { name: ServiceLoggerProperties };
  user: {
    orgId: string | number;
    id: string | number;
    email?: never;
  };
};

type PropertiesMappingFunction = (
  properties: SplunkLoggerProperties,
) => SplunkLoggerProperties;

export type SetAutoflushOptions = 'auto' | 'manual' | undefined;

const ENDPOINT = process.env.NX_SPLUNK_LOG_ENDPOINT as string;
const TOKEN = process.env.NX_SPLUNK_API_TOKEN as string;
const FLUSH_INTERVAL = 10000;
export const MISSING_VALUE = 'VALUE_MISSING';

export default class SplunkLogger {
  static INSTANCE = new SplunkLogger();

  renderLogCache = new Set<string>();

  console: { [key in ConsoleMethodName]: (..._: unknown[]) => void } = {
    log: console.log,
    info: console.info,
    warn: console.warn,
    error: console.error,
  };

  properties = {
    service: {
      name: 'Unconfigured',
    },
  };

  logQueue: SplunkEventTimestamped[] = [];

  flushInterval: ReturnType<typeof setInterval> | undefined;

  // Prevent external instances
  private constructor() {
    Object.keys(this.console).forEach(method =>
      this.monkeyPatchConsole(method as ConsoleMethodName),
    );

    this.startFlushInterval();
  }

  startFlushInterval(): void {
    if (!this.flushInterval) {
      this.flushInterval = setInterval(() => this.flush(), FLUSH_INTERVAL);
    }
  }

  clearFlushInterval(): void {
    if (this.flushInterval) {
      clearInterval(this.flushInterval);
      this.flushInterval = undefined;
    }
  }

  getLogIndex(): 'prod' | 'nonprod' {
    if (process.env.DEPLOY_ENV === 'production') {
      return 'prod';
    }
    return 'nonprod';
  }

  formatEventsForSplunkBatch(): string {
    return this.logQueue
      .filter(
        event =>
          event.tag !== 'console' ||
          (event.tag === 'console' &&
            event.eventPayload.message !== null &&
            event.eventPayload.message !== undefined),
      )
      .map(event =>
        JSON.stringify({
          event: {
            ...this.properties,
            ...event.eventPayload,
          },
          time: event.clientTimestamp,
          index: this.getLogIndex(),
          sourcetype: '_json',
        }),
      )
      .join('\n');
  }

  async flush(): Promise<void> {
    try {
      if (this.logQueue.length === 0) {
        return;
      }

      await ky.post(`${ENDPOINT}`, {
        body: this.formatEventsForSplunkBatch(),
        headers: {
          Authorization: `Splunk ${TOKEN}`,
          'Content-Type': 'application/json',
        },
      });

      this.logQueue.length = 0;
    } catch (e) {
      this.console.error(e);
    }
  }

  pushEvent(event: SplunkEvent): void {
    if (process.env.NODE_ENV === 'development' && event.tag !== 'console') {
      this.console[event.eventPayload.level](
        JSON.stringify(event.eventPayload),
      );
    }
    this.logQueue.push({
      ...event,
      clientTimestamp: Date.now(),
    });
  }

  monkeyPatchConsole(method: ConsoleMethodName): void {
    console[method] = (...args: unknown[]): void => {
      this.console[method].apply(console, args);
      this.pushEvent({
        tag: 'console',
        eventPayload: {
          level: method,
          message: args,
        },
      });
    };
  }

  configureLogger(
    properties: SplunkLoggerProperties | PropertiesMappingFunction,
    setAutoflush: SetAutoflushOptions = undefined,
  ): void {
    if (typeof properties === 'function') {
      this.properties = properties(this.properties as SplunkLoggerProperties);
    } else {
      this.properties = properties;
    }
    switch (setAutoflush) {
      case 'auto':
        this.startFlushInterval();
        break;
      case 'manual':
        this.clearFlushInterval();
        break;
      default:
    }
  }

  observeInternalEvent(
    namespacedAction: NamespacedAction,
    data: InternalEventData,
    level: ConsoleMethodName = 'log',
  ): void {
    this.pushEvent({
      tag: 'internalObserve',
      eventPayload: {
        ...namespacedAction,
        data,
        level,
      },
    });
  }

  observeInternalError(
    namespacedAction: NamespacedAction,
    data: InternalEventData,
    e?: Error,
  ): void {
    const error = {
      message: 'none',
      name: 'none',
      stack: 'none',
    };
    if (e && e instanceof Error) {
      error.message = e.message;
      error.name = e.name;
      error.stack = e.stack || 'none';
    }
    this.pushEvent({
      tag: 'internalError',
      eventPayload: {
        ...namespacedAction,
        data,
        level: 'error',
        error,
      },
    });
  }

  componentRenderLog(
    action: 'Inject' | 'Render',
    featureSet: ExtensionFeatureSet,
    componentName: ExtensionComponents,
  ): void {
    try {
      if (!SplunkLogger.INSTANCE.renderLogCache.has(componentName)) {
        SplunkLogger.INSTANCE.renderLogCache.add(componentName);
        this.observeInternalEvent(
          { namespace: 'Component', action },
          {
            featureSet,
            component: componentName,
          },
        );
      }
    } catch (e) {
      this.console.error(e);
    }
  }
}

export const configureLogger = (
  properties: SplunkLoggerProperties | PropertiesMappingFunction,
  setAutoflush: SetAutoflushOptions = undefined,
): void => SplunkLogger.INSTANCE.configureLogger(properties, setAutoflush);

export const flush = (): Promise<void> => SplunkLogger.INSTANCE.flush();

export const observeInternalEvent = (
  namespacedAction: NamespacedAction,
  data: InternalEventData,
  level: ConsoleMethodName = 'log',
): void =>
  SplunkLogger.INSTANCE.observeInternalEvent(namespacedAction, data, level);

export const observeInternalError = (
  namespacedAction: NamespacedAction,
  data: InternalEventData,
  e?: Error,
): void =>
  SplunkLogger.INSTANCE.observeInternalError(namespacedAction, data, e);

export const componentRenderLog = (
  action: 'Inject' | 'Render',
  feature: ExtensionFeatureSet,
  componentName: ExtensionComponents,
): void => {
  SplunkLogger.INSTANCE.componentRenderLog(action, feature, componentName);
};
